import React, { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import '../../styles/global.css';

/**
 * "Drukarki" tab: network label printers (ZPL over TCP). Each production line prints its
 * weighed-spool labels on the one printer it is assigned to here.
 */
interface LineRef {
  id: number;
  name: string;
}

interface Printer {
  id: number;
  name: string;
  host: string;
  port: number;
  dpi: number;
  is_active: boolean;
  production_lines: LineRef[];
}

interface PrinterForm {
  name: string;
  host: string;
  port: string;
  dpi: string;
  is_active: boolean;
  lineIds: number[];
}

const EMPTY_FORM: PrinterForm = { name: '', host: '', port: '9100', dpi: '203', is_active: true, lineIds: [] };

const headerCell: React.CSSProperties = {
  padding: '16px 12px', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px',
};

const fieldLabel: React.CSSProperties = { display: 'block', marginBottom: 16, fontWeight: 500, color: '#4a5568' };

const errorMessage = (error: any, fallback: string): string => {
  const errors = error?.response?.data?.errors;
  const first = errors ? (Object.values(errors)[0] as string[] | undefined)?.[0] : undefined;
  return first || error?.response?.data?.message || fallback;
};

const Printers: React.FC = () => {
  const { t } = useLanguage();
  const [printers, setPrinters] = useState<Printer[]>([]);
  const [lines, setLines] = useState<LineRef[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Printer | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState<PrinterForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<number | null>(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [printersRes, linesRes] = await Promise.all([
        api.get<{ data: Printer[] }>('/printers'),
        api.get<LineRef[]>('/production-lines'),
      ]);
      setPrinters(printersRes.data.data || []);
      setLines((linesRes.data || []).map((l) => ({ id: l.id, name: l.name })));
    } catch {
      alert(t('printers.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Which printer each line prints on — a line taken by another printer cannot be picked here.
  const lineOwner = useMemo(() => {
    const owner = new Map<number, Printer>();
    printers.forEach((p) => p.production_lines.forEach((l) => owner.set(l.id, p)));
    return owner;
  }, [printers]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (printer: Printer) => {
    setEditing(printer);
    setForm({
      name: printer.name,
      host: printer.host,
      port: String(printer.port),
      dpi: String(printer.dpi),
      is_active: printer.is_active,
      lineIds: printer.production_lines.map((l) => l.id),
    });
    setShowModal(true);
  };

  const payloadOf = (f: PrinterForm) => ({
    name: f.name,
    host: f.host,
    port: parseInt(f.port, 10) || 0,
    dpi: parseInt(f.dpi, 10) || 203,
    is_active: f.is_active,
    production_line_ids: f.lineIds,
  });

  const formOf = (p: Printer): PrinterForm => ({
    name: p.name, host: p.host, port: String(p.port), dpi: String(p.dpi), is_active: p.is_active,
    lineIds: p.production_lines.map((l) => l.id),
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/printers/${editing.id}`, payloadOf(form));
      } else {
        await api.post('/printers', payloadOf(form));
      }
      setShowModal(false);
      fetchAll();
    } catch (error: any) {
      alert(errorMessage(error, t('printers.saveError')));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (printer: Printer) => {
    try {
      await api.put(`/printers/${printer.id}`, payloadOf({ ...formOf(printer), is_active: !printer.is_active }));
      fetchAll();
    } catch (error: any) {
      alert(errorMessage(error, t('printers.saveError')));
    }
  };

  const handleDelete = async (printer: Printer) => {
    if (!window.confirm(t('printers.confirmDelete', { name: printer.name }))) return;
    try {
      await api.delete(`/printers/${printer.id}`);
      fetchAll();
    } catch (error: any) {
      alert(errorMessage(error, t('printers.deleteError')));
    }
  };

  const handleTest = async (printer: Printer) => {
    setTesting(printer.id);
    try {
      const res = await api.post<{ message: string }>(`/printers/${printer.id}/test`);
      alert(res.data.message);
    } catch (error: any) {
      alert(errorMessage(error, t('printers.testError')));
    } finally {
      setTesting(null);
    }
  };

  const toggleLine = (lineId: number) => {
    setForm((f) => ({
      ...f,
      lineIds: f.lineIds.includes(lineId) ? f.lineIds.filter((id) => id !== lineId) : [...f.lineIds, lineId],
    }));
  };

  const smallButton = (color: string, filled: boolean): React.CSSProperties => ({
    padding: '6px 12px', fontSize: 13,
    backgroundColor: filled ? color : 'white', color: filled ? 'white' : color, border: `2px solid ${color}`,
  });

  return (
    <div>
      <div className="page-header" style={{ padding: '8px 0', marginBottom: 12 }}>
        <h1 style={{ color: '#2d3748', background: 'none', WebkitTextFillColor: '#2d3748' }}>
          {t('printers.title')}
        </h1>
        <button onClick={openCreate} className="btn" style={{ backgroundColor: '#667eea', color: '#fff', fontWeight: 600 }}>
          + {t('printers.add')}
        </button>
      </div>
      <p style={{ color: '#4a5568', fontSize: 14, marginTop: 0, marginBottom: 20 }}>{t('printers.help')}</p>

      {loading ? (
        <div className="loading-spinner"><div className="spinner" /></div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
              <thead>
                <tr style={{ backgroundColor: '#667eea', color: 'white' }}>
                  <th style={{ ...headerCell, textAlign: 'left' }}>{t('printers.name')}</th>
                  <th style={{ ...headerCell, textAlign: 'left' }}>{t('printers.address')}</th>
                  <th style={{ ...headerCell, textAlign: 'left' }}>{t('printers.lines')}</th>
                  <th style={{ ...headerCell, textAlign: 'center' }}>{t('printers.status')}</th>
                  <th style={{ ...headerCell, textAlign: 'center' }}>{t('printers.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {printers.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ padding: '48px 20px', textAlign: 'center', color: '#718096', fontStyle: 'italic' }}>
                      {t('printers.empty')}
                    </td>
                  </tr>
                )}
                {printers.map((p) => (
                  <tr key={p.id} style={{ borderBottom: '1px solid #e1e8ed', backgroundColor: p.is_active ? 'white' : '#f8f9fa' }}>
                    <td style={{ padding: '14px 12px', fontWeight: 600, color: '#2d3748' }}>{p.name}</td>
                    <td style={{ padding: '14px 12px', color: '#4a5568', fontFamily: 'monospace' }}>
                      {p.host}:{p.port}
                      <div style={{ fontSize: 12, color: '#718096', fontFamily: 'inherit' }}>{p.dpi} dpi</div>
                    </td>
                    <td style={{ padding: '14px 12px' }}>
                      {p.production_lines.length === 0 ? (
                        <span style={{ color: '#a0aec0', fontStyle: 'italic' }}>{t('printers.noLines')}</span>
                      ) : (
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {p.production_lines.map((l) => (
                            <span key={l.id} style={{ padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, backgroundColor: '#eef0fd', color: '#4c51bf' }}>
                              {l.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '14px 12px', textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block', padding: '4px 12px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                        backgroundColor: p.is_active ? '#d1fae5' : '#fee2e2', color: p.is_active ? '#065f46' : '#991b1b',
                      }}>
                        {p.is_active ? t('printers.active') : t('printers.inactive')}
                      </span>
                    </td>
                    <td style={{ padding: '14px 12px' }}>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button className="btn" style={smallButton('#667eea', false)} onClick={() => openEdit(p)}>
                          {t('common.edit')}
                        </button>
                        <button className="btn" style={smallButton('#667eea', true)} disabled={testing === p.id} onClick={() => handleTest(p)}>
                          {testing === p.id ? t('printers.testing') : t('printers.test')}
                        </button>
                        <button className="btn" style={smallButton(p.is_active ? '#f59e0b' : '#10b981', true)} onClick={() => toggleActive(p)}>
                          {p.is_active ? t('printers.deactivate') : t('printers.activate')}
                        </button>
                        <button className="btn" style={smallButton('#ef4444', true)} onClick={() => handleDelete(p)}>
                          {t('common.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: 16,
        }}>
          <div style={{ backgroundColor: 'white', padding: 30, borderRadius: 12, maxWidth: 520, width: '100%', maxHeight: 'calc(100vh - 32px)', overflowY: 'auto' }}>
            <h2 style={{ marginTop: 0, color: '#667eea' }}>
              {editing ? t('printers.editTitle') : t('printers.addTitle')}
            </h2>
            <form onSubmit={handleSubmit}>
              <label style={fieldLabel}>
                {t('printers.name')} *
                <input type="text" className="input" value={form.name} required maxLength={255}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={t('printers.namePlaceholder')} style={{ marginTop: 8 }} />
              </label>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <label style={{ ...fieldLabel, flex: '1 1 220px' }}>
                  {t('printers.host')} *
                  <input type="text" className="input" value={form.host} required maxLength={253}
                    onChange={(e) => setForm({ ...form, host: e.target.value })}
                    placeholder="192.168.1.50" style={{ marginTop: 8 }} />
                </label>
                <label style={{ ...fieldLabel, flex: '0 1 110px' }}>
                  {t('printers.port')} *
                  <input type="number" className="input" value={form.port} required min={1} max={65535}
                    onChange={(e) => setForm({ ...form, port: e.target.value })} style={{ marginTop: 8 }} />
                </label>
                <label style={{ ...fieldLabel, flex: '0 1 110px' }}>
                  {t('printers.dpi')}
                  <select className="input" value={form.dpi} onChange={(e) => setForm({ ...form, dpi: e.target.value })} style={{ marginTop: 8 }}>
                    <option value="203">203 dpi</option>
                    <option value="300">300 dpi</option>
                  </select>
                </label>
              </div>
              <p style={{ fontSize: 12, color: '#718096', marginTop: -8, marginBottom: 16 }}>{t('printers.hostHelp')}</p>

              <div style={{ ...fieldLabel, marginBottom: 8 }}>{t('printers.lines')}</div>
              <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, marginBottom: 16, maxHeight: 220, overflowY: 'auto' }}>
                {lines.length === 0 && <div style={{ color: '#a0aec0', fontStyle: 'italic' }}>{t('printers.noLinesDefined')}</div>}
                {lines.map((l) => {
                  const owner = lineOwner.get(l.id);
                  const takenElsewhere = !!owner && owner.id !== editing?.id;
                  return (
                    <label key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px', cursor: takenElsewhere ? 'not-allowed' : 'pointer', color: takenElsewhere ? '#a0aec0' : '#2d3748' }}>
                      <input type="checkbox" checked={form.lineIds.includes(l.id)} disabled={takenElsewhere} onChange={() => toggleLine(l.id)} />
                      <span style={{ fontWeight: 600 }}>{l.name}</span>
                      {takenElsewhere && (
                        <span style={{ fontSize: 12 }}>{t('printers.lineTaken', { printer: owner!.name })}</span>
                      )}
                    </label>
                  );
                })}
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, cursor: 'pointer', color: '#4a5568' }}>
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                {t('printers.activeLabel')}
              </label>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" className="btn" style={{ backgroundColor: '#6c757d', color: 'white' }} onClick={() => setShowModal(false)}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn" disabled={saving} style={{ backgroundColor: '#667eea', color: 'white' }}>
                  {editing ? t('common.update') : t('common.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Printers;
